import type { Common } from "@beautypie/segment"
import cookie from "js-cookie"

import type { CountryType } from "lib/apollo/types"
import { Logger } from "lib/logger"
import { getSession } from "session"
import { getInitialAnonId } from "lib/apollo/initialvals"
import { getBPFingerprint } from "lib/fingerprint"
import { parseCampaign } from "./campaign"
import { parseDevice } from "./device"
import { parseTraits } from "./traits"
import { parseOS } from "./os"
import { parsePageDetails } from "./page"
import { isMigrated } from "./identifier"
import { searchTermFromURL } from "./search"
import { parseExperimentFlags } from "./experiment-flags"
import { getQueryParams } from "./queryParams"

export const getContext = async (
  locale: CountryType
): Promise<Common["context"] | undefined> => {
  const store = locale === "us" ? "BeautyPie US" : "BeautyPie UK"
  const [session, traits] = await Promise.all([getSession(), parseTraits()])

  const sessionId = session?.id
  const visitNumber = session?.count
  // The legacySessionId is required only as long as the data team is moving across to use fingerprint as the long term identifier of users for reporting purposes. It exists side-by-side with the newer, fingerprint derived, sessionId on all events
  const legacySessionId = session
    ? `${getInitialAnonId()}:${visitNumber}`
    : undefined

  const log = Logger("data/tracking/segment/getContext")

  const searchTerm = searchTermFromURL()
  const queryStringObject = getQueryParams(window.location.href)

  try {
    return {
      locale,
      store,
      event_id: "",
      fbp: cookie.get("_fbp"),
      fbc: cookie.get("_fbc"),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator?.userAgent,
      campaign: parseCampaign(),
      device: parseDevice(),
      os: parseOS(),
      page: parsePageDetails(locale),
      traits,
      ...(isMigrated() ? { migrated: true } : {}),
      fingerprintId: await getBPFingerprint().then(
        (result) => result?.fingerprintId
      ),
      sessionId,
      visitNumber,
      // @todo: remove this once confirmed by all teams
      experiementFlags: parseExperimentFlags(),
      legacySessionId,
      queryStringObject,
      experienceId: cookie.get("experience_id"),
      testIdentity: cookie.get("test_identity"),
      platform: "web",

      ...(searchTerm
        ? {
            search: {
              term: searchTerm,
            },
          }
        : {}),
    }
  } catch (e) {
    log.error(`Error retrieving context: ${JSON.stringify(e)}`)
  }
}
